import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import EducatorLemonBotCard from "@/components/EducatorLemonBotCard";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import ResourceLibrary from "@/components/ResourceLibrary";
import {
  EDUCATOR_RESOURCES,
  EDUCATOR_RESOURCES_FOLDER,
} from "@/data/externalResources";
import { Settings } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EducatorDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newClass, setNewClass] = useState({ name: "", description: "" });
  const [totalStudents, setTotalStudents] = useState(0);
  const [totalLessons, setTotalLessons] = useState(0);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
      
      // Calculate totals
      const students = response.data.reduce((sum, cls) => sum + (cls.student_count || 0), 0);
      setTotalStudents(students);
      
      // Load progress for all classes
      let lessons = 0;
      for (const cls of response.data) {
        try {
          const progressRes = await axios.get(`${API}/classes/${cls.id}/progress`);
          lessons += progressRes.data.reduce((sum, s) => sum + s.lessons_completed, 0);
        } catch (err) {
          console.error(`Error loading progress for class ${cls.id}:`, err);
        }
      }
      setTotalLessons(lessons);
    } catch (err) {
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/classes`, newClass);
      setNewClass({ name: "", description: "" });
      setShowCreateModal(false);
      loadClasses();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create class");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        <AnnouncementBanner role={user?.role} />
        {/* Welcome Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2" data-testid="welcome-message">
                Welcome, {user.name}! 👩‍🏫
              </h1>
              <p className="text-gray-600">Manage your classes and track student progress</p>
            </div>
          </div>
        </div>

        {/* Summary Metrics */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-gray-600 text-sm font-semibold mb-2">Total Classes</div>
            <div className="text-5xl font-bold text-green-600">{classes.length}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-gray-600 text-sm font-semibold mb-2">Total Students</div>
            <div className="text-5xl font-bold text-blue-600">{totalStudents}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-gray-600 text-sm font-semibold mb-2">Lessons Completed</div>
            <div className="text-5xl font-bold text-purple-600">{totalLessons}</div>
          </div>
        </div>

        {/* Classes Section */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">My Classes</h2>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => navigate("/game/host")}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                data-testid="launch-game-btn"
              >
                🎮 Launch Live Game
              </button>
              <button
                onClick={() => navigate("/game/reports")}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                data-testid="game-reports-btn"
              >
                📊 Game Reports
              </button>
              <button
                onClick={() => navigate("/educator/cte-metrics")}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                data-testid="cte-metrics-btn"
                title="Perkins / CLNA-ready Lesson 1 metrics & CSV export"
              >
                📈 CTE Metrics & CSV
              </button>
              <button
                onClick={() => navigate("/feedback")}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                data-testid="feedback-center-btn"
              >
                📝 Feedback Center
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="educator-button px-6 py-3 rounded-lg text-white font-semibold"
                data-testid="create-class-btn"
              >
                + Create New Class
              </button>
            </div>
          </div>

          {classes.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">🏫</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">No Classes Yet</h3>
              <p className="text-gray-600 mb-6">Create your first class to get started</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="educator-button px-6 py-3 rounded-lg text-white font-semibold"
              >
                Create Your First Class
              </button>
            </div>
          ) : (
            <>
              <EducatorLemonBotCard classes={classes} />
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {classes.map((cls) => (
                <div
                  key={cls.id}
                  className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all"
                  data-testid={`class-card-${cls.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 
                      className="text-xl font-bold text-gray-800 flex-1 cursor-pointer hover:text-yellow-700"
                      onClick={() => navigate(`/classes/${cls.id}`)}
                    >
                      {cls.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {cls.student_count} students
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/class/${cls.id}/settings`);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Class Settings"
                        data-testid={`class-settings-btn-${cls.id}`}
                      >
                        <Settings className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <p 
                    className="text-gray-600 text-sm mb-4 cursor-pointer"
                    onClick={() => navigate(`/classes/${cls.id}`)}
                  >
                    {cls.description || "No description"}
                  </p>
                  <div 
                    className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3 cursor-pointer"
                    onClick={() => navigate(`/classes/${cls.id}`)}
                  >
                    <div className="text-xs text-gray-600 mb-1">Join Code</div>
                    <div className="text-2xl font-bold text-yellow-700 tracking-wider" data-testid={`join-code-${cls.id}`}>
                      {cls.join_code}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>

        {/* Educator Resources — Drive folder with facilitator guides */}
        <div className="mt-10">
          <ResourceLibrary
            title="Educator Resources"
            description="Facilitator guides, classroom activities, and event-planning kits for The Lemonade Stand program. Open the folder in Drive to download."
            folderUrl={EDUCATOR_RESOURCES_FOLDER}
            files={EDUCATOR_RESOURCES}
            testId="educator-resources"
            accent="sky"
          />
        </div>
      </div>

      {/* Create Class Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Create New Class</h2>
            <form onSubmit={handleCreateClass}>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">Class Name</label>
                <input
                  type="text"
                  value={newClass.name}
                  onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-green-400"
                  placeholder="e.g., 5th Grade Entrepreneurs"
                  required
                  data-testid="class-name-input"
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">Description (Optional)</label>
                <textarea
                  value={newClass.description}
                  onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-green-400"
                  placeholder="Brief description of your class"
                  rows="3"
                  data-testid="class-description-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                  data-testid="cancel-create-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 educator-button px-4 py-3 rounded-lg text-white font-semibold"
                  data-testid="submit-create-btn"
                >
                  Create Class
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EducatorDashboard;
